import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { documentService, userService } from '../../services/api';
import { FiDownload, FiTrash2, FiPlus } from 'react-icons/fi';
import { format, parseISO, isBefore, startOfDay } from 'date-fns';
import { DocumentUpload } from './DocumentUpload';
import './DocumentList.css';

const DOCUMENT_CATEGORIES = [
  { id: 'address-proof', label: 'Address Proof' },
  { id: 'id-proof', label: 'ID Proof' },
  { id: 'passport', label: 'Passport' },
  { id: 'visa', label: 'Visa' },
  { id: 'other', label: 'Other' },
];

export const DocumentList = () => {
  const { user } = useAuth();
  const isReadOnly = user?.isActive === false;
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(user?.id || '');

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'manager') {
      fetchEmployees();
    }
  }, [user?.role]);

  useEffect(() => {
    fetchDocuments();
  }, [selectedEmployee]);

  const fetchEmployees = async () => {
    try {
      const response = await userService.getAll({ limit: 1000 });
      setEmployees(response.data.users || []);
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await documentService.getAll({ userId: selectedEmployee });
      setDocuments(response.data.documents || []);
    } catch (err) {
      setError('Failed to load documents');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      await documentService.delete(documentId);
      setSuccess('Document deleted successfully');
      fetchDocuments();
    } catch (err) {
      setError('Failed to delete document');
      console.error(err);
    }
  };

  const handleDownloadDocument = async (documentId, filename) => {
    try {
      const response = await documentService.download(documentId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'document');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download document');
      console.error(err);
    }
  };

  const isDocumentExpired = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = parseISO(expiryDate);
    return isBefore(expiry, startOfDay(new Date()));
  };

  const isDocumentExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = parseISO(expiryDate);
    const today = startOfDay(new Date());
    const thirtyDaysFromNow = new Date(today);
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return isBefore(expiry, thirtyDaysFromNow) && !isBefore(expiry, today);
  };

  const getDocumentsForCategory = (categoryId) => {
    return documents.filter((doc) => doc.documentType === categoryId);
  };

  const groupedDocuments = DOCUMENT_CATEGORIES.map((category) => ({
    ...category,
    docs: getDocumentsForCategory(category.id),
  }));

  const getCategoryIcon = (categoryId) => {
    const icons = {
      'address-proof': '📍',
      'id-proof': '🆔',
      passport: '📕',
      visa: '📋',
      other: '📄',
    };
    return icons[categoryId] || '📄';
  };

  return (
    <div className="documents-list-container">
      <div className="documents-header">
        <h1>Documents</h1>
        {!isReadOnly && (
          <button
            className="btn btn-primary"
            onClick={() => setShowUploadModal(true)}
          >
            <FiPlus /> Upload Document
          </button>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {(user?.role === 'admin' || user?.role === 'manager') && (
        <div className="employee-filter">
          <label>Employee:</label>
          <select
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
          >
            <option value={user?.id}>My Documents</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.firstName} {emp.lastName}
              </option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading documents...</div>
      ) : (
        <div className="documents-categories">
          {groupedDocuments.map((category) => (
            <div key={category.id} className="category-section">
              <h2 className="category-title">
                <span className="category-icon">{getCategoryIcon(category.id)}</span>
                {category.label}
              </h2>

              {category.docs.length > 0 ? (
                <div className="documents-grid">
                  {category.docs.map((doc) => (
                    <div
                      key={doc.id}
                      className={`document-card ${
                        isDocumentExpired(doc.expiryDate) ? 'expired' : ''
                      } ${
                        isDocumentExpiringSoon(doc.expiryDate) ? 'expiring-soon' : ''
                      }`}
                    >
                      {isDocumentExpired(doc.expiryDate) && (
                        <div className="expired-badge">Expired</div>
                      )}
                      {isDocumentExpiringSoon(doc.expiryDate) && (
                        <div className="expiring-badge">Expiring Soon</div>
                      )}

                      <div className="document-info">
                        <h3 className="document-filename">{doc.originalName}</h3>

                        <div className="document-details">
                          <div className="detail-item">
                            <span className="detail-label">Uploaded:</span>
                            <span className="detail-value">
                              {format(parseISO(doc.uploadDate), 'MMM d, yyyy')}
                            </span>
                          </div>

                          {doc.expiryDate && (
                            <div className="detail-item">
                              <span className="detail-label">Expires:</span>
                              <span
                                className={`detail-value ${
                                  isDocumentExpired(doc.expiryDate) ? 'error' : ''
                                }`}
                              >
                                {format(parseISO(doc.expiryDate), 'MMM d, yyyy')}
                              </span>
                            </div>
                          )}

                          {doc.notes && (
                            <div className="detail-item full-width">
                              <span className="detail-label">Notes:</span>
                              <span className="detail-value">{doc.notes}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="document-actions">
                        <button
                          className="btn-action btn-download"
                          onClick={() =>
                            handleDownloadDocument(doc.id, doc.originalName)
                          }
                          title="Download document"
                        >
                          <FiDownload /> Download
                        </button>
                        {!isReadOnly && (
                          <button
                            className="btn-action btn-delete"
                            onClick={() => handleDeleteDocument(doc.id)}
                            title="Delete document"
                          >
                            <FiTrash2 /> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-category">
                  No documents in this category
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showUploadModal && (
        <DocumentUpload
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            fetchDocuments();
            setSuccess('Document uploaded successfully');
          }}
          onError={(err) => setError(err)}
        />
      )}
    </div>
  );
};

export default DocumentList;
